import { RAW_TYPES } from "./constants-GZCtrBU_.js";
import { uuidv7 } from "uuidv7";

//#region src/logger.ts
const SPOTLIGHT_PREFIX = "🔎 [Spotlight]";
const defaultLogger = {
	info: (message) => console.error(SPOTLIGHT_PREFIX, message),
	warn: (message) => console.error(SPOTLIGHT_PREFIX, message),
	error: (message) => console.error(SPOTLIGHT_PREFIX, message),
	debug: (message) => debugEnabled && console.error(SPOTLIGHT_PREFIX, message)
};
let injectedLogger = void 0;
let debugEnabled = false;
function activateLogger(logger$1) {
	injectedLogger = logger$1;
}
function enableDebugLogging(debug) {
	debugEnabled = debug;
}
function isDebugEnabled() {
	return debugEnabled;
}
const logger = {
	info: (message) => (injectedLogger || defaultLogger).info(message),
	warn: (message) => (injectedLogger || defaultLogger).warn(message),
	error: (message) => (injectedLogger || defaultLogger).error(message),
	debug: (message) => (injectedLogger || defaultLogger).debug(message)
};

//#endregion
//#region src/parser/helpers.ts
const ERROR_EVENT_TYPES = new Set(["event", "error"]);
const TRACE_EVENT_TYPES = new Set(["transaction"]);
const PROFILE_EVENT_TYPES = new Set(["profile"]);
const LOG_EVENT_TYPES = new Set(["log"]);
const SUPPORTED_EVENT_TYPES = new Set([
	...ERROR_EVENT_TYPES,
	...TRACE_EVENT_TYPES,
	...PROFILE_EVENT_TYPES,
	...LOG_EVENT_TYPES
]);
function isErrorEvent(event) {
	return (!event.type || ERROR_EVENT_TYPES.has(event.type)) && Boolean(event.exception);
}
function isProfileEvent(event) {
	return !!event.type && PROFILE_EVENT_TYPES.has(event.type) && event.version === "1";
}
function isTraceEvent(event) {
	return !!event.type && TRACE_EVENT_TYPES.has(event.type);
}
function isLogEvent(event) {
	return !!event.type && LOG_EVENT_TYPES.has(event.type);
}

//#endregion
//#region src/parser/processEnvelope.ts
const TEXT_CONTENT_TYPES = new Set([
	"text/plain",
	"text/css",
	"text/csv",
	"text/html",
	"text/javascript",
	"text/json",
	"text/x-json",
	"application/json",
	"application/ld+json",
	"application/json"
]);
/**
* Implements parser for
* @see https://develop.sentry.dev/sdk/envelopes/#serialization-format
* @param rawEvent Envelope data
* @returns parsed envelope
*/
function processEnvelope(rawEvent) {
	let buffer = typeof rawEvent.data === "string" ? Uint8Array.from(rawEvent.data, (c) => c.charCodeAt(0)) : rawEvent.data;
	function readLine(length) {
		const cursor = length ?? getLineEnd(buffer);
		const line = buffer.subarray(0, cursor);
		buffer = buffer.subarray(cursor + 1);
		return line;
	}
	const envelopeHeader = parseJSONFromBuffer(readLine());
	if (!envelopeHeader) {
		logger.error("Malformed envelope header, skipping...");
		return null;
	}
	const envelopeId = uuidv7();
	envelopeHeader.__spotlight_envelope_id = envelopeId;
	const items = [];
	while (buffer.length) {
		const itemHeader = parseJSONFromBuffer(readLine());
		const payloadLength = itemHeader.length;
		const itemPayloadRaw = readLine(payloadLength);
		let itemPayload;
		try {
			if (RAW_TYPES.has(itemHeader.type)) {
				const rawPayload = itemPayloadRaw;
				if (!itemHeader.content_type && itemHeader.type === "statsd") itemHeader.content_type = "text/plain";
				itemPayload = { data: rawPayload.toString(TEXT_CONTENT_TYPES.has(itemHeader.content_type) ? "utf-8" : "base64") };
			} else {
				itemPayload = parseJSONFromBuffer(itemPayloadRaw);
				if (!itemPayload) logger.error(itemHeader);
				else if (itemHeader.type) itemPayload.type = itemHeader.type;
			}
		} catch (err) {
			itemPayload = itemPayloadRaw;
			logger.error(err);
		}
		items.push([itemHeader, itemPayload]);
	}
	const envelope = [envelopeHeader, items];
	return {
		event: envelope,
		rawEvent
	};
}
function getLineEnd(data) {
	let end = data.indexOf(10);
	if (end === -1) end = data.length;
	return end;
}
function parseJSONFromBuffer(data) {
	try {
		return JSON.parse(new TextDecoder().decode(data));
	} catch (err) {
		logger.error(err);
		return null;
	}
}

//#endregion
export { ERROR_EVENT_TYPES, LOG_EVENT_TYPES, PROFILE_EVENT_TYPES, SUPPORTED_EVENT_TYPES, TRACE_EVENT_TYPES, activateLogger, enableDebugLogging, isDebugEnabled, isErrorEvent, isLogEvent, isProfileEvent, isTraceEvent, logger, processEnvelope };
//# sourceMappingURL=parser-BnBZzLYl.js.map